@if (supplierBaskets?.length) {
    <div class="basket-summary">
        <!-- Addresses -->
        @if (hasAddress(invoiceAddress) || hasAddress(deliveryAddress)) {
            <div class="row g-3 mb-3">
                @if (hasAddress(invoiceAddress)) {
                    <div class="col-12 col-md-6">
                        <div class="basket-summary__block h-100">
                            <div class="basket-summary__label">
                                <em class="fas fa-id-badge pe-2"></em>{{ 'Rechnungsadresse' | translate }}
                            </div>
                            <ng-container
                                *ngTemplateOutlet="addressTpl; context: { $implicit: invoiceAddress }"
                            ></ng-container>
                        </div>
                    </div>
                }
                @if (hasAddress(deliveryAddress)) {
                    <div class="col-12 col-md-6">
                        <div class="basket-summary__block h-100">
                            <div class="basket-summary__label">
                                <em class="fas fa-map-marked-alt pe-2"></em>{{ 'Lieferadresse' | translate }}
                            </div>
                            <ng-container
                                *ngTemplateOutlet="addressTpl; context: { $implicit: deliveryAddress }"
                            ></ng-container>
                        </div>
                    </div>
                }
            </div>
        }

        <!-- Per supplier -->
        @for (supplierBasket of supplierBaskets; track supplierBasket.id) {
            <div class="basket-summary__supplier mb-3">
                <div class="basket-summary__supplier-header">
                    <em class="fas fa-truck pe-2"></em>
                    <span>{{
                        supplierBasket.supplier?.name!
                            | supplierDisplayName: supplierBasket.supplier?.supplierNumber
                    }}</span>
                </div>

                @if (supplierBasket.supplierNote?.note) {
                    <div class="alert alert-info py-2 mb-3">
                        <strong class="me-1">{{ 'Bestellhinweis des Lieferanten' | translate }}:</strong>
                        <span>{{ supplierBasket.supplierNote.note }}</span>
                    </div>
                }

                <!-- Items -->
                @for (item of sortedItems(supplierBasket); track item.id) {
                    <div class="basket-summary__item">
                        @if (thumbnailSrc(item)) {
                            <image class="basket-summary__thumb" [image]="thumbnailSrc(item)"></image>
                        }
                        <div class="basket-summary__item-main">
                            <div class="basket-summary__item-name">{{ item.product?.name | stringPreview: 150 }}</div>
                            <div class="basket-summary__item-meta">
                                <span>{{ item.quantity?.value }} {{ item.product?.orderUnit | unit }}</span>
                                @if (item.product?.unitPrice) {
                                    <span class="basket-summary__dot">·</span>
                                    <span>
                                        <display-currency [value]="item.product?.unitPrice?.amount" [currencyCode]="item.product?.unitPrice?.currency" />
                                    </span>
                                }
                            </div>

                            @if (hasItemDetails(item) || purchasingGroupLabel(supplierBasket, item)) {
                                <dl class="basket-summary__details">
                                    @if (item.product?.supplierProductNumber) {
                                        <dt>{{ 'Artikelnummer Lieferant' | translate }}</dt>
                                        <dd>{{ item.product.supplierProductNumber }}</dd>
                                    }
                                    @if (item.product?.customerProductNumber) {
                                        <dt>{{ 'Artikelnummer' | translate }}</dt>
                                        <dd>{{ item.product.customerProductNumber }}</dd>
                                    }
                                    @if (item.product?.manufacturer?.name) {
                                        <dt>{{ 'Hersteller' | translate }}</dt>
                                        <dd>{{ item.product.manufacturer.name }}</dd>
                                    }
                                    @if (item.product?.manufacturer?.productNumber) {
                                        <dt>{{ 'Artikelnummer Hersteller' | translate }}</dt>
                                        <dd>{{ item.product.manufacturer.productNumber }}</dd>
                                    }
                                    @if (showContentUnit(item)) {
                                        <dt>{{ 'Inhaltseinheit' | translate }}</dt>
                                        <dd>{{ item.product.contentUnit | unit }}</dd>
                                    }
                                    @if (item.desiredDeliveryDate) {
                                        <dt>{{ 'Gewünschtes Lieferdatum' | translate }}</dt>
                                        <dd>{{ item.desiredDeliveryDate | dayjs }}</dd>
                                    }
                                    @if (item.generalLedgerAccount?.key) {
                                        <dt>{{ 'Sachkonto' | translate }}</dt>
                                        <dd>
                                            {{ item.generalLedgerAccount.key }}
                                            @if (item.generalLedgerAccount.name) {
                                                – {{ item.generalLedgerAccount.name }}
                                            }
                                        </dd>
                                    }
                                    @if (item.costCenter?.key) {
                                        <dt>{{ 'Kostenstelle' | translate }}</dt>
                                        <dd>
                                            {{ item.costCenter.key }}
                                            @if (item.costCenter.name) {
                                                – {{ item.costCenter.name }}
                                            }
                                        </dd>
                                    }
                                    @if (item.project?.key) {
                                        <dt>{{ 'Projekt' | translate }}</dt>
                                        <dd>
                                            {{ item.project.key }}
                                            @if (item.project.name) {
                                                – {{ item.project.name }}
                                            }
                                        </dd>
                                    }
                                    @if (item.customAccountAssignments?.length) {
                                        @for (custom of item.customAccountAssignments; track custom.id) {
                                            @if (custom?.key) {
                                                <dt>{{ custom.typeName || ('Zusätzliche Kontierung' | translate) }}</dt>
                                                <dd>
                                                    @if (custom.displayProperty === 'NAME') {
                                                        {{ custom.name || custom.key }}
                                                    } @else {
                                                        {{ custom.key }}
                                                        @if (custom.name) {
                                                            – {{ custom.name }}
                                                        }
                                                    }
                                                </dd>
                                            }
                                        }
                                    }
                                    @if (item.classificationCode?.code) {
                                        <dt>{{ 'Klassifizierung' | translate }}</dt>
                                        <dd>
                                            {{ item.classificationCode.code }}
                                            @if (item.classificationCode.name) {
                                                – {{ item.classificationCode.name }}
                                            }
                                        </dd>
                                    }
                                    @if (item.materialGroup?.code) {
                                        <dt>{{ 'Warengruppe' | translate }}</dt>
                                        <dd>
                                            {{ item.materialGroup?.code }}
                                            @let materialGroupLabel = materialGroupName(item);
                                            @if (materialGroupLabel) {
                                                – {{ materialGroupLabel }}
                                            }
                                        </dd>
                                    }
                                    @if (purchasingGroupLabel(supplierBasket, item); as purchasingGroup) {
                                        <dt>{{ 'Einkaufsgruppe' | translate }}</dt>
                                        <dd>{{ purchasingGroup }}</dd>
                                    }
                                    @if (item.siteCode) {
                                        <dt>{{ 'Standort' | translate }}</dt>
                                        <dd>
                                            {{ item.siteCode }}
                                            @if (item.siteName) {
                                                – {{ item.siteName }}
                                            }
                                        </dd>
                                    }
                                    @if (item.internalNote) {
                                        <dt>{{ 'Interne Notiz' | translate }}</dt>
                                        <dd>{{ item.internalNote }}</dd>
                                    }
                                </dl>
                            }

                            @if (item.attachments?.length) {
                                <div class="basket-summary__item-attachments">
                                    <div class="basket-summary__label">
                                        <em class="fas fa-paperclip pe-1"></em>{{ 'Anhänge' | translate }}
                                    </div>
                                    <ul class="basket-summary__attachment-list mb-0">
                                        @for (file of item.attachments; track file.id) {
                                            <li>
                                                @if (attachmentLink(file)) {
                                                    <a [href]="attachmentLink(file)" target="_blank" rel="noopener">
                                                        {{ attachmentName(file) }}
                                                    </a>
                                                } @else {
                                                    {{ attachmentName(file) }}
                                                }
                                            </li>
                                        }
                                    </ul>
                                </div>
                            }
                        </div>
                        <div class="basket-summary__item-sum">
                            <display-currency [value]="item.price?.amount" [currencyCode]="item.price?.currency" />
                        </div>
                    </div>
                }

                <!-- Conditions -->
                @if (supplierBasket.paymentTerm || supplierBasket.frameContract) {
                    <dl class="basket-summary__conditions">
                        @if (supplierBasket.paymentTerm) {
                            <dt>{{ 'Zahlungsbedingung' | translate }}</dt>
                            <dd>
                                {{ supplierBasket.paymentTerm.acronym }}
                                @if (supplierBasket.paymentTerm.description) {
                                    – {{ supplierBasket.paymentTerm.description }}
                                }
                            </dd>
                        }
                        @if (supplierBasket.frameContract) {
                            <dt>{{ 'Rahmenvertrag' | translate }}</dt>
                            <dd>
                                {{ supplierBasket.frameContract.contractNumber }}
                                @if (supplierBasket.frameContract.title) {
                                    – {{ supplierBasket.frameContract.title }}
                                }
                            </dd>
                        }
                    </dl>
                }

                <!-- Supplier contacts (chosen in the address step, UC_BA_1021) -->
                @if (supplierBasket.supplierContacts?.length) {
                    <dl class="basket-summary__conditions">
                        <dt>{{ 'Ansprechpartner' | translate }}</dt>
                        <dd>
                            @for (contact of supplierBasket.supplierContacts; track contact.realSupplierContactId) {
                                <div>
                                    {{ contact.firstName }} {{ contact.surName }}
                                    @if (contact.email) {
                                        ({{ contact.email }})
                                    }
                                </div>
                            }
                        </dd>
                    </dl>
                }

                <!-- Transmission flags (mirrors the toggles set in the address step) -->
                @if (
                    supplierBasket.transmissionInfo?.preventTransferToClientErp ||
                    supplierBasket.transmissionInfo?.preventTransferToSupplier
                ) {
                    <div class="basket-summary__transmission">
                        @if (supplierBasket.transmissionInfo?.preventTransferToClientErp) {
                            <div>
                                <em class="fas fa-ban pe-1"></em>
                                {{ 'Bestellung nicht an ERP System übertragen' | translate }}
                            </div>
                        }
                        @if (supplierBasket.transmissionInfo?.preventTransferToSupplier) {
                            <div>
                                <em class="fas fa-ban pe-1"></em>
                                {{ 'Bestellung nicht an den Lieferanten übertragen' | translate }}
                            </div>
                        }
                    </div>
                }

                <!-- Messages -->
                @if (supplierBasket.commentForSupplier) {
                    <div class="basket-summary__message">
                        <div class="basket-summary__label">{{ 'Nachricht an den Lieferanten' | translate }}</div>
                        <p class="mb-0">{{ supplierBasket.commentForSupplier }}</p>
                    </div>
                }
                @if (supplierBasket.commentForApprover) {
                    <div class="basket-summary__message">
                        <div class="basket-summary__label">{{ 'Kommentar an Freigeber' | translate }}</div>
                        <p class="mb-0">{{ supplierBasket.commentForApprover }}</p>
                    </div>
                }

                <!-- Attachments -->
                @if (supplierBasket.attachments?.length) {
                    <div class="basket-summary__attachments">
                        <div class="basket-summary__label">{{ 'Anhänge' | translate }}</div>
                        <ul class="mb-0">
                            @for (file of supplierBasket.attachments; track file.id) {
                                <li>
                                    <em class="fas fa-paperclip pe-1"></em>
                                    @if (attachmentLink(file)) {
                                        <a [href]="attachmentLink(file)" target="_blank" rel="noopener">
                                            {{ attachmentName(file) }}
                                        </a>
                                    } @else {
                                        {{ attachmentName(file) }}
                                    }
                                </li>
                            }
                        </ul>
                    </div>
                }

                <!-- Totals -->
                <div class="basket-summary__totals">
                    @if (supplierBasket.totalFreightCosts) {
                        <div class="d-flex justify-content-between">
                            <span>{{ 'Lieferkosten' | translate }}:</span>
                            <span>
                                <display-currency [value]="supplierBasket.totalFreightCosts.amount" [currencyCode]="supplierBasket.totalFreightCosts.currency" />
                            </span>
                        </div>
                    }
                    <div class="d-flex justify-content-between basket-summary__total">
                        <strong>{{ 'Summe netto' | translate }}
                            {{
                                supplierBasket.supplier?.name!
                                    | supplierDisplayName: supplierBasket.supplier?.supplierNumber
                            }}:
                        </strong>
                        <strong><display-currency [value]="supplierBasket.total?.amount" [currencyCode]="supplierBasket.total?.currency" /></strong>
                    </div>
                </div>
            </div>
        }
        <basket-currency-totals [currencyTotals]="currencyTotals" />
    </div>
}

<ng-template #addressTpl let-address>
    <div class="basket-summary__address">
        @if (address?.contactInfo?.customerInfo?.firstName || address?.contactInfo?.customerInfo?.lastName) {
            <div class="fw-semibold">
                {{ address?.contactInfo?.customerInfo?.firstName }}
                {{ address?.contactInfo?.customerInfo?.lastName }}
            </div>
        }
        @if (address?.contactInfo?.companyInfo?.name) {
            <div>{{ address.contactInfo.companyInfo.name }}</div>
        }
        @if (address?.contactInfo?.companyInfo?.name2) {
            <div>{{ address.contactInfo.companyInfo.name2 }}</div>
        }
        @if (address?.address?.street) {
            <div>{{ address.address.street }}</div>
        }
        @if (address?.address?.zip || address?.address?.city) {
            <div>{{ address.address.zip }} {{ address.address.city }}</div>
        }
        @if (address?.address?.country) {
            <div>{{ address.address.country | alpha2CountryCode | async }}</div>
        }
    </div>
</ng-template>
